import { Injectable, inject, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, throwError, finalize } from 'rxjs';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';
import { CookieService } from './cookie.service';
import { PermissionService } from './permission.service';
import { EncryptionService } from './encryption.service';
import { NotificationHubService } from './notification-hub.service';

export interface User {
  userId?: string;
  email: string;
  fullname?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  roleId?: number | string;
  roleName?: string;
  autoLogOutOnIdleTime?: number;
  [key: string]: any;
}

export interface LoginResponse {
  status: boolean;
  message: string;
  data?: {
    userId: string;
    email: string;
    fullname: string;
    role: string;
    [key: string]: any;
  };
}

export interface VerifyOtpResponse {
  status: boolean;
  message: string;
  data?: {
    token: string;
    userId: string;
    email: string;
    fullname: string;
    role: string;
    autoLogOutOnIdleTime?: number;
    [key: string]: any;
  };
}

export interface LogoutResponse {
  status: boolean;
  message: string;
}

export interface ForgotPasswordResponse {
  status: boolean;
  message: string;
  data?: any;
}

export interface ResetPasswordResponse {
  status: boolean;
  message: string;
  data?: any;
}

export interface VerifyTokenResponse {
  status: boolean;
  message: string;
  data: {
    userId: string;
    emailId: string;
  } | null;
}

export interface RefreshTokenResponse {
  status: boolean;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private permissionService = inject(PermissionService);
  private encryptionService = inject(EncryptionService);
  private modalService = inject(NgbModal);
  private injector = inject(Injector);
  
  private apiUrl = environment.apiBaseUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private temporaryEmailSubject = new BehaviorSubject<string>('');
  public temporaryEmail$ = this.temporaryEmailSubject.asObservable();

  private temporaryUserIdSubject = new BehaviorSubject<string>('');
  public temporaryUserId$ = this.temporaryUserIdSubject.asObservable();

  constructor() {
    // Load from cookies/localStorage if exists
    const token = this.getToken();
    const savedUser = this.getUserFromStorage();
    if (savedUser) {
      this.currentUserSubject.next(savedUser);
      this.isAuthenticatedSubject.next(true);
    }
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const url = `${this.apiUrl}account/Login`;
    const payload = { email, password };
    
    console.log('Login API URL:', url);
    console.log('Login payload:', { email, password: '***' });
    
    return this.http.post<LoginResponse>(url, payload).pipe(
      tap(response => {
        console.log('Login API response:', response);
        if (response.status && response.data) {
          // Store userId and email temporarily for OTP verification (encrypted)
          this.temporaryUserIdSubject.next(response.data.userId);
          this.temporaryEmailSubject.next(response.data.email);
          this.encryptionService.setItem('tempUserId', response.data.userId);
          this.encryptionService.setItem('tempEmail', response.data.email);
        }
      })
    );
  }

  verifyOtp(userId: string, otp: string): Observable<VerifyOtpResponse> {
    const url = `${this.apiUrl}account/otp/verify`;
    const payload = { userId, otp };
    
    console.log('Verify OTP API URL:', url);
    console.log('Verify OTP payload:', payload);
    
    return this.http.post<VerifyOtpResponse>(url, payload).pipe(
      tap(response => {
        console.log('Verify OTP API response:', response);
        if (response.status && response.data) {
          const { userId, email, fullname, role, roleId, roleName, autoLogOutOnIdleTime } = response.data;
          const user: User = { 
            userId, 
            email, 
            fullname, 
            role,
            roleId: roleId || response.data['roleId'],
            roleName: roleName || response.data['roleName'],
            autoLogOutOnIdleTime: autoLogOutOnIdleTime || response.data['autoLogOutOnIdleTime']
          };
          
          this.currentUserSubject.next(user);
          this.isAuthenticatedSubject.next(true);
          this.saveUserToStorage(user);
          this.encryptionService.removeItem('tempEmail');
          this.encryptionService.removeItem('tempUserId');
        }
      })
    );
  }

  logout(): Observable<any> {
    const url = `${this.apiUrl}account/logout`;
    const token = this.getToken();
    
    console.log('Logout API URL:', url);
    console.log('Token present:', !!token);
    
    return this.http.post<LogoutResponse>(url, {}, { withCredentials: true }).pipe(
      tap(response => {
        console.log('Logout API response:', response);
      }),
      catchError((error) => {
        console.warn('Logout API error (ignoring):', error);
        return new Observable(observer => {
          observer.next({ status: true, message: 'Logged out locally' });
          observer.complete();
        });
      }),
      finalize(() => this.clearAuthData())
    );
  }

  // Public method to clear auth data (used by interceptor for 401 errors)
  clearAuthData(): void {
    // Close all open modals before clearing auth data
    this.modalService.dismissAll();
    
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.encryptionService.removeItem('currentUser');
    this.cookieService.deleteCookie('AiBillingSID');
    this.cookieService.deleteCookie('AiBillingRefreshSID');
    this.cookieService.deleteCookie('token');
    this.cookieService.deleteCookie('refreshToken');
    this.encryptionService.removeItem('tempEmail');
    this.encryptionService.removeItem('tempUserId');
    this.encryptionService.removeSessionItem('selectedClinicId');
    // Clear permissions
    this.permissionService.clearPermissions();
    // Disconnect SignalR notification hub
    this.injector.get(NotificationHubService).shutdown();
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    const url = `${this.apiUrl}account/token/refresh`;
    const user = this.getCurrentUser();
    const payload = { userId: user?.userId };

    return this.http.post<RefreshTokenResponse>(url, payload, { withCredentials: true }).pipe(
      catchError((error) => {
        console.warn('Refresh token API failed:', error);
        return throwError(() => error);
      })
    );
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  getToken(): string | null {
    return this.cookieService.getCookie('AiBillingSID') ?? null;
  }

  getUserFromStorage(): User | null {
    try {
      return this.encryptionService.getItem('currentUser');
    } catch (e) {
      console.error('Error retrieving saved user:', e);
      return null;
    }
  }

  saveUserToStorage(user: User): void {
    this.encryptionService.setItem('currentUser', user);
  }

  getTempEmail(): string {
    return this.encryptionService.getItem('tempEmail') || this.temporaryEmailSubject.value;
  }

  getTempUserId(): string {
    return this.encryptionService.getItem('tempUserId') || this.temporaryUserIdSubject.value;
  }

  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    const url = `${this.apiUrl}account/password/forgot`;
    const payload = { email };
    
    console.log('Forgot Password API URL:', url);
    console.log('Forgot Password payload:', payload);
    
    return this.http.post<ForgotPasswordResponse>(url, payload).pipe(
      tap(response => {
        console.log('Forgot Password API response:', response);
      })
    );
  }

  verifyToken(token: string, emailId: string): Observable<VerifyTokenResponse> {
    const url = `${this.apiUrl}account/password/verifytoken`;
    const payload = { token, emailId };
    return this.http.post<VerifyTokenResponse>(url, payload);
  }

  resetPassword(email: string, emailToken: string, confirmPassword: string): Observable<ResetPasswordResponse> {
    const url = `${this.apiUrl}account/password/reset`;
    const payload = { email, emailToken, confirmPassword, password: confirmPassword, isEncrypted: true };
    
    console.log('Reset Password API URL:', url);
    console.log('Reset Password payload:', { email, emailToken, confirmPassword: '***' });
    
    return this.http.post<ResetPasswordResponse>(url, payload).pipe(
      tap(response => {
        console.log('Reset Password API response:', response);
      })
    );
  }
}
